import * as React from 'react';
import { FilterEditProps, FilterProviderBase, isGenericEmptyValue, QueryStringTarget } from '.';
import SingleAutoSuggestFilterEdit from '../editors/SingleAutoSuggestFilterEdit';
import { Filter, FilterString } from '..';
import {
	ValueLabelDelimiter,
	MultipleValueDelimiter,
	NotNullValue,
	NullValue,
	NullValueLabel,
	NotNullValueLabel,
} from './Constants';
import MultipleAutoSuggestFilterEdit from '../editors/MultipleAutoSuggestFilterEdit';

// prop definitions for auto suggest field edit components.
export interface AutoSuggestFilterEditProps extends FilterEditProps<AutoSuggestValue> {}
export interface AutoSuggestArrayFilterEditProps extends FilterEditProps<AutoSuggestValue[]> {}
export interface AutoSuggestValue {
	value: string;
	label: string;
}

const getValidAutoSuggestValueFromQueryString = (query: string, filter: Filter): AutoSuggestValue => {
	const values = query.split(ValueLabelDelimiter);

	if (values[0] === NullValue) {
		return {
			value: NullValue,
			label: filter.options && filter.options.nullValueLabel ? filter.options.nullValueLabel : NullValueLabel,
		};
	}

	if (values[0] === NotNullValue) {
		return { value: NotNullValue, label: NotNullValueLabel };
	}

	if (values.length !== 2) {
		throw new Error('Invalid AutoSuggest value found: ' + query);
	}

	// eslint-disable-next-line
	let [value, label] = values;

	if (isGenericEmptyValue(value)) {
		throw new Error('Invalid AutoSuggest value found: ' + query);
	}

	if (isGenericEmptyValue(label)) {
		label = value;
	}

	return { value, label };
};

/**
 * Provider for single auto suggest values. Full reimplementation of the base contract to handle
 * AutoSuggest values properly.
 *
 * @export
 * @class SingleAutoSuggestValueFieldProvider
 * @implements {FieldProviderBase<AutoSuggestValue>}
 */
export class SingleAutoSuggestValueFilterProvider implements FilterProviderBase<AutoSuggestValue> {
	public getComponent = (): React.ComponentType => SingleAutoSuggestFilterEdit;
	// eslint-disable-next-line no-unused-vars
	public getComponentProps = (filter: Filter) => ({});
	public getFriendlyValueForDisplay = (value: AutoSuggestValue): React.ReactNode => {
		if (!value || (!value.label && !value.value)) {
			return '<Empty>';
		}

		if (value.label) {
			return value.label;
		}

		return value.value;
	};
	public getValueFromQueryString = (query: string, filter: Filter) => {
		if (!query) {
			return null;
		}

		return getValidAutoSuggestValueFromQueryString(query, filter);
	};
	// eslint-disable-next-line no-unused-vars
	public getQueryStringFromValue = (value: AutoSuggestValue, filter: Filter, target: QueryStringTarget) => {
		if (!value) {
			return '';
		}

		return value.value + ValueLabelDelimiter + value.label;
	};
	public isEmptyValue = (value: AutoSuggestValue): boolean => {
		if (isGenericEmptyValue(value)) {
			return true;
		}

		if (isGenericEmptyValue(value.value)) {
			return true;
		}

		return false;
	};
}

/**
 * Provider for multiple auto suggest values. Full reimplementation of the base contract to handle
 * AutoSuggest values properly.
 *
 * @export
 * @class MultipleAutoSuggestValueFieldProvider
 * @implements {(FieldProviderBase<AutoSuggestValue[] | null>)}
 */
export class MultipleAutoSuggestValueFilterProvider implements FilterProviderBase<AutoSuggestValue[] | null> {
	public getComponent = (): React.ComponentType => MultipleAutoSuggestFilterEdit;
	// eslint-disable-next-line no-unused-vars
	public getComponentProps = (filter: Filter) => ({});
	public getFriendlyValueForDisplay = (values: AutoSuggestValue[]): React.ReactNode => {
		const getFriendlyValueForSingle = (singleValue: AutoSuggestValue) => {
			if (!singleValue || (!singleValue.label && !singleValue.value)) {
				return '<Empty>';
			}

			if (singleValue.label) {
				return FilterString(singleValue.label);
			}

			return singleValue.value;
		};

		if (!values || values.length === 0) {
			return '<Empty>';
		}

		return values.map((value) => getFriendlyValueForSingle(value)).join(', ');
	};
	public getValueFromQueryString = (query: string, filter: Filter) => {
		if (!query) {
			return null;
		}

		const result: AutoSuggestValue[] = [];
		const autoSuggestPairs = query.split(MultipleValueDelimiter);

		// take a look at tests to see what this is doing.
		for (const pair of autoSuggestPairs) {
			try {
				result.push(getValidAutoSuggestValueFromQueryString(pair, filter));
			} catch (error) {}
		}

		return result;
	};
	public getQueryStringFromValue = (
		values: AutoSuggestValue[],
		// eslint-disable-next-line no-unused-vars
		filter: Filter,
		// eslint-disable-next-line no-unused-vars
		target: QueryStringTarget,
	): string => {
		if (!values || values.length === 0) {
			return '';
		}

		return values
			.map((value) => {
				if (value.value === NotNullValue || value.value === NullValue) {
					return value.value;
				}

				return value.value + ValueLabelDelimiter + value.label;
			})
			.join(MultipleValueDelimiter);
	};
	public isEmptyValue = (value: AutoSuggestValue[]): boolean => {
		if (isGenericEmptyValue(value)) {
			return true;
		}

		if (
			Array.isArray(value) &&
			(value.length === 0 || value.filter((val) => !isGenericEmptyValue(val)).length === 0)
		) {
			return true;
		}

		return false;
	};
}
